'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Mail } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong');
      }

      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-brand-black px-4 py-16">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] opacity-10"
          style={{ background: 'radial-gradient(ellipse, #C4D600 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
      </div>

      {/* Logo */}
      <Link href="/" className="flex items-center gap-3 mb-10">
        <div className="w-10 h-10 rounded-full bg-brand-lime flex items-center justify-center">
          <span className="text-brand-black font-bold">SW</span>
        </div>
        <span className="font-semibold text-lg text-brand-white tracking-wide">STRANGER WORLD</span>
      </Link>

      <div className="relative w-full max-w-md bg-surface-elevated rounded-2xl border border-white/10 p-8 shadow-modal">
        <Link
          href="/login"
          className="inline-flex items-center gap-1.5 text-sm text-brand-gray hover:text-brand-white transition-colors mb-6"
        >
          <ArrowLeft size={14} />
          Back to sign in
        </Link>

        {submitted ? (
          <div className="flex flex-col items-center text-center gap-4 py-4">
            <div className="w-14 h-14 rounded-full bg-brand-lime/10 border border-brand-lime/30 flex items-center justify-center">
              <Mail size={24} className="text-brand-lime" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-brand-white">Check your email</h1>
              <p className="text-sm text-brand-gray mt-2">
                If <span className="text-brand-white font-medium">{email}</span> is registered, we&apos;ve sent a password reset link. It expires in 1 hour.
              </p>
            </div>
            <Link href="/login">
              <Button variant="secondary">Back to sign in</Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-brand-white">Forgot password?</h1>
              <p className="text-sm text-brand-gray mt-1">
                Enter your email and we&apos;ll send you a reset link.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
              />

              {error && (
                <p className="text-sm text-brand-red bg-brand-red/10 border border-brand-red/20 rounded-xl px-4 py-2.5">
                  {error}
                </p>
              )}

              <Button type="submit" fullWidth loading={loading} size="md" className="mt-2">
                Send Reset Link
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
